import * as React from "react";
import { useInstanceState } from "../instance/instance-store";
import { DEFAULT_TITLE, useTitleContext } from "../../contexts/TitleContext";
import { useRouteType } from "../../hooks/useRouteType";
import { QuickAgentChatPanel } from "./QuickAgentChatPanel";

/**
 * Wrapper around QuickAgentChatPanel that manages visibility and constructs
 * a one-time initialPrompt with page and instance context for the chat agent.
 *
 * Visibility rules:
 * - Search pages: sidebar auto-collapsed (chat rendered inline in SearchHome)
 * - Admin pages: panel completely hidden
 * - Other pages: collapsed sidebar tab
 *
 * Context is a COLD-START-ONLY input. On the first render where instance + a
 * usable title (or a non-page route) are available, we build the initialPrompt
 * and FREEZE it for the lifetime of this component instance. Later renders —
 * e.g. the page title changing (or resetting to the default) during in-app
 * navigation, or an in-tab instance switch — must NOT rebuild it: the iframe
 * src is derived from this value, so changing it would remount the iframe and
 * discard the live conversation. Fresh context only happens when a brand-new
 * frame is created (full reload / new tab), which remounts this component and
 * re-runs the capture from scratch.
 */
const PROMPT_PREFIX = "This is silent context to be passed to the chat agent, the user is currently";
const PROMPT_SUFFIX = "Introduce yourself to the user by using their first name and tell them your name and what your capabilities are and ask if they need any help navigating PathFinder with you.";

export const QuickAgentChatPanelWrapper: React.FC<{ agentUrl: string }> = ({ agentUrl }) => {
    const { isAdmin, isSearch } = useRouteType();
    const { instanceId } = useInstanceState();
    const { title } = useTitleContext();

    // The candidate prompt for THIS render. Returns undefined until the cold-start
    // context is ready (instance present, and on a page route the real page title
    // has been set). This may change across renders as the title changes — the
    // latch below captures only the first ready value and ignores the rest.
    const candidateQuery = React.useMemo(() => {
        if (!instanceId) return undefined;

        const hasTitle = title && title !== DEFAULT_TITLE;
        if (hasTitle) {
            return `${PROMPT_PREFIX} on the page "${title}" on the ${instanceId} instance. ${PROMPT_SUFFIX}`;
        }

        // On non-page routes the title is never customized, use instance-only context
        const isPageRoute = /^#\/page\//.test(window.location.hash);
        if (!isPageRoute) {
            return `${PROMPT_PREFIX} on the ${instanceId} instance. ${PROMPT_SUFFIX}`;
        }

        // On page routes, wait for title to be set by the page component
        return undefined;
    }, [instanceId, title]);

    // Latch the first ready candidate and never change it again. Storing it in a
    // ref (not state) means updating it does not itself trigger a re-render; the
    // re-render that reveals the ready value is already driven by the instanceId/
    // title change that produced it.
    const frozenQuery = React.useRef<string | undefined>(undefined);
    if (frozenQuery.current === undefined && candidateQuery !== undefined) {
        frozenQuery.current = candidateQuery;
    }

    // Hide completely on admin pages. (Admin is configuration-only and is opened
    // in a new tab, so this does not unmount an active chat on a content page.)
    if (isAdmin) {
        return null;
    }

    // Wait until the cold-start context has been captured before mounting the iframe.
    if (frozenQuery.current === undefined) {
        return null;
    }

    return (
        <QuickAgentChatPanel
            agentUrl={agentUrl}
            initialQuery={frozenQuery.current}
            defaultCollapsed={true}
            autoCollapse={isSearch}
        />
    );
};
